import axiosInstance from '../../config/axios.config';
import { SET_ADMIN_DATA } from '../type';
import { load, success } from './loader.action';

/**
 * Action creator для установки данных сущности в Redux store.
 *
 * @param {Object} payload - объект с ключом и данными
 * @param {string} payload.stateName - ключ в state.adminData, например 'roads'
 * @param {Array|Object} payload.data - данные, полученные с сервера
 * @returns {Object} Action с типом SET_ADMIN_DATA и переданным payload
 */
export const setAdminData = (payload) => ({
  type: SET_ADMIN_DATA,
  payload,
});

/**
 * Загружает данные для административной панели и записывает в Redux.
 *
 * @param {string} stateName - ключ в Redux, под которым будут храниться данные
 * @param {string} apiPath - URL для запроса
 */
export const getDataForAdmin = (stateName, apiPath) => async (dispatch) => {
  try {
    // Делаем GET-запрос
    const response = await axiosInstance.get(apiPath);

    // Если пришли данные — диспатчим
    if (response?.data) {
      dispatch(setAdminData({ stateName, data: response.data }));
    }
  } catch (error) {
    console.error(`Ошибка при получении данных для ${stateName}:`, error);
  }
};

/**
 * Создаёт thunk для добавления новой сущности через POST-запрос.
 *
 * @param {string} entityKey - ключ сущности в Redux (например: 'roads')
 * @param {string} apiPath - путь к API (например: '/api/administration/roads')
 * @returns {Function} thunk, принимающий formData
 */
export const createEntityAction = (entityKey, apiPath) => (formData) => async (dispatch) => {
  try {
    dispatch(load()); // Устанавливаем состояние загрузки

    const response = await axiosInstance.post(apiPath, formData);

    // Если успешный ответ (200 или 201), обновляем данные и ставим флаг успеха
    if (response.status === 200 || response.status === 201) {
      dispatch(success()); // Успешная операция
      dispatch(getDataForAdmin(entityKey, apiPath)); // Обновляем данные в Redux
    }
  } catch (error) {
    console.error(`Ошибка при добавлении ${entityKey}:`, error);
  }
};

/**
 * Кастомный экшен для добавления email-адресов к рассылке.
 *
 * Выполняет POST-запрос по пути `/api/administration/mailings/{mailingKey}/emails`
 * с данными формы и затем обновляет список рассылок через стандартный `getDataForAdmin`.
 *
 * @function
 * @param {Object} formData - Данные формы, включая `mailingKey` и email-адреса.
 * @returns {Function} Redux thunk-функция
 *
 * @example
 * dispatch(addMailingWithEmails({
 *   mailingKey: 'update-notify',
 *   emails: ['test@example.com']
 * }));
 */
export const addMailingWithEmails = (formData) => async (dispatch) => {
  try {
    dispatch(load());

    const mailingKey = formData?.keyName;
    const url = `/api/administration/mailings/${mailingKey}/emails`;

    const response = await axiosInstance.post(url, formData);

    if (response.status === 200 || response.status === 201) {
      dispatch(success());
      dispatch(getDataForAdmin('mailings', '/api/administration/mailings'));
    }
  } catch (error) {
    console.error(`Ошибка при добавлении mailings:`, error);
  }
};

/**
 * Удаляет email из конкретной рассылки
 *
 * Выполняет DELETE-запрос по адресу `/api/administration/mailings/{mailingKey}/emails/{id}`.
 * После успешного удаления обновляет список рассылок.
 *
 * @function
 * @param {Object} payload - Объект с параметрами.
 * @param {string} payload.mailingKey - Ключ рассылки.
 * @param {number|string} payload.id - ID email-записи для удаления.
 * @returns {Function} Redux thunk-функция
 *
 * @example
 * dispatch(deleteEmailFromMailing({ mailingKey: 'updates', id: 123 }));
 */
export const deleteEmailFromMailing = (id, data) => async (dispatch) => {
  try {
    dispatch(load());

    const url = `/api/administration/mailings/${data?.keyName}/emails/${data?.id}`;
    const response = await axiosInstance.delete(url);

    if (response.status === 200 || response.status === 204) {
      dispatch(success());
      dispatch(getDataForAdmin('mailings', '/api/administration/mailings'));
    }
  } catch (error) {
    console.error(`Ошибка при удалении email из рассылки ${data?.keyName}:`, error);
  }
};
/**
 * Создаёт thunk для редактирования сущности через PATCH-запрос.
 *
 * @param {string} entityKey - ключ сущности в Redux
 * @param {string} apiPath - путь к API с {id} (например: '/api/administration/roads/{id}')
 * @returns {Function} thunk, принимающий formData и id
 */
export const updateEntityAction = (entityKey, apiPath) => (formData, id) => async (dispatch) => {
  try {
    dispatch(load());
    // Подставляем id в URL
    const url = `${apiPath}/${id}`;
    const response = await axiosInstance.patch(url, formData);

    if (response.status === 200) {
      dispatch(success());
      dispatch(getDataForAdmin(entityKey, apiPath)); // Обновляем данные после редактирования
    }
  } catch (error) {
    console.error(`Ошибка при обновлении ${entityKey}:`, error);
  }
};

/**
 * Создаёт thunk для удаления сущности через DELETE-запрос.
 *
 * @param {string} entityKey - ключ сущности в Redux
 * @param {string} apiPath - путь к API с  {id} (например: '/api/administration/roads/{id}')
 * @returns {Function} thunk, принимающий id
 */
export const deleteEntityAction = (entityKey, apiPath) => (id, data) => async (dispatch) => {
  try {
    dispatch(load());
    // Подставляем id в URL
    const url = `${apiPath}/${id || data?.id}`;
    const response = await axiosInstance.delete(url);

    if (response.status === 200) {
      dispatch(success());
      dispatch(getDataForAdmin(entityKey, apiPath)); // Обновляем данные после удаления
    }
  } catch (error) {
    console.error(`Ошибка при удалении ${entityKey}:`, error);
  }
};
